import type { ColDef } from 'ag-grid-community';
import { useContext, useEffect, useState } from 'react';
import AccountContext from '../contexts/AccountContext';
import { defaultFormatter } from '../components/Grid/findCellValueFormatter';
import {
  CONTRACT_ON_TARGET_CELL_FIELD,
  CONTRACT_PROGRESS_CELL_FIELD,
  ON_TARGET_CELL_FIELD,
  PROGRESS_CELL_FIELD,
  PROGRESS_CELL_TYPE,
} from '../components/Grid/gridConstants';
import getBaseViewFields from '../components/Grid/getBaseViewFields';
import BoardContext from '../contexts/BoardContext';
import _ from 'lodash';
import isDefined from '../isDefined';
import isCustomerLaneBoard from '../isCustomerLaneBoard';
import isGeneralBoard from '../isBoardWithSlideOut';
import {
  BOOLEAN_ICON_CELL,
  DATE_CELL,
  DRILL_DOWN_CELL,
  ENTITY_CELL,
  GEO_POINT_CELL,
  NUMBER_CELL,
  BONUS_EXPECTATION_CELL,
  BONUS_REWARD_CELL,
  BONUS_SCORE_CELL,
  BONUS_STATUS_CELL,
  BONUS_QUALIFIED_CELL,
  COLOUR_CODED_NUMBER_CELL,
} from '../components/Grid/constants';
import { CUSTOMER_LANE_COMMITMENTS } from '../constants';
import PerformanceBoardContexts from '../components/PerformanceBoards/contexts';
import isPerformanceBoard from '../isPerformanceBoard';
import { PERFORMANCE_ENTITY_CELL } from '../components/PerformanceBoards/constants';
import { isPerformanceColumn } from './useBoardBaseView';
import DatasetDefinitionsContext from '../contexts/DatasetDefinitionsContext';
import EntityDefinitionsContext from '../contexts/EntityDefinitionsContext';
import { FleetOpsCellRendererParams } from '../types/agGrid';
import RolesContext from '../contexts/RolesContext';
import getFieldType from '../utils/getFieldType';
import PERMISSIONS from '../permissionDefinitions';
import captureException from '../services/captureException';
import isDriverPerformanceBoard from '../isDriverPerformanceBoard';

export const getSpecialCellField = (cell: BoardSpecialCell): string => {
  if (cell.type === 'OnTarget') {
    return cell.headerName
      ? cell.headerName
      : `${cell.differenceField} - On Target`;
  } else {
    return cell.headerName
      ? cell.headerName
      : `${cell.differenceField} - Progress`;
  }
};

export const getType = (format: string | undefined) => {
  switch (format) {
    case 'Number':
    case 'Int':
    case 'float':
    case 'Float':
    case 'long':
    case 'Long':
    case 'Currency':
      return 'numericColumn';
    default:
      return undefined;
  }
};

export const getLaneCommitCols = () => [
  {
    field: PROGRESS_CELL_FIELD,
    type: 'numericColumn',
    valueFormatter: defaultFormatter,
    cellRenderer: 'progressCell',
    headerClass: 'rightAlignedHeader',
    minWidth: 80,
    flex: 1,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    menuTabs: [],
    cellRendererParams: {
      cell: {
        type: PROGRESS_CELL_TYPE,
        cadenceField: 'cadence',
        totalField: 'totalLoads',
        targetField: 'commitment',
        differenceField: 'differenceInProgress',
        headerName: 'Progress',
        tooltipTitle: 'Loads Completed',
      },
    },
  },
  {
    field: CONTRACT_PROGRESS_CELL_FIELD,
    type: 'numericColumn',
    valueFormatter: defaultFormatter,
    cellRenderer: 'contractProgressCell',
    headerClass: 'rightAlignedHeader',
    minWidth: 80,
    flex: 1,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    menuTabs: [],
  },
  {
    field: ON_TARGET_CELL_FIELD,
    type: 'numericColumn',
    valueFormatter: defaultFormatter,
    cellRenderer: 'onTargetCell',
    headerClass: 'rightAlignedHeader',
    minWidth: 80,
    flex: 1,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    cellRendererParams: {
      cell: {
        type: 'OnTarget',
        differenceField: 'differenceInProgress',
        headerName: 'On Target',
      },
    },
    menuTabs: [],
  },
  {
    field: CONTRACT_ON_TARGET_CELL_FIELD,
    type: 'numericColumn',
    valueFormatter: defaultFormatter,
    cellRenderer: 'contractOnTargetCell',
    headerClass: 'rightAlignedHeader',
    minWidth: 80,
    flex: 1,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    menuTabs: [],
  },
];

export const buildProgressCell = ({
  cell,
  baseView,
}: {
  cell: BoardProgressCell;
  baseView: FleetOps.BaseView;
}) => ({
  field: getSpecialCellField(cell),
  type: 'numericColumn',
  valueFormatter: defaultFormatter,
  cellRenderer: 'progressCell',
  minWidth: 80,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellRendererParams: {
    cell,
    fieldView: (() => {
      const maybeF = getBaseViewFields(baseView).find(
        (f) => f.field === cell.totalField,
      );
      if (maybeF) {
        return maybeF;
      }
      return undefined;
    })(),
  },
});

export const getCellRenderer = ({
  isLaneCell,
  fieldView,
  isCustomerLaneBoard,
  isPerformanceBoard,
  entityField,
  board,
  isEntityField,
  getEntityAndDefaultAppForField,
}: {
  isLaneCell: boolean;
  fieldView: FleetOps.BaseViewField;
  isCustomerLaneBoard: boolean;
  isPerformanceBoard: boolean;
  entityField?: string;
  board?: Board | PerformanceBoardTypes.Board;
  isEntityField: boolean;
  getEntityAndDefaultAppForField: ({ field }: { field: string }) =>
    | {
        entity: EntityDetails.Entity;
        app: EntityDetails.App;
      }
    | undefined;
}) => {
  if (isPerformanceBoard && !!entityField && fieldView.field === entityField) {
    return {
      cellRenderer: PERFORMANCE_ENTITY_CELL,
      isLeftAlign: true,
    };
  }
  if (
    board &&
    isGeneralBoard(board) &&
    fieldView.field === board.drillDownField
  ) {
    return {
      cellRenderer: DRILL_DOWN_CELL,
      isLeftAlign: true,
      entityDefinition: getEntityAndDefaultAppForField({
        field: fieldView.field,
      }),
    };
  }

  if (isDriverPerformanceBoard(board) && fieldView.field === 'driver') {
    return { cellRenderer: 'driverCell', isLeftAlign: true };
  }

  if (fieldView.cellType === 'Bonus Score Field') {
    return { cellRenderer: BONUS_SCORE_CELL, isLeftAlign: false };
  }

  if (fieldView.cellType === 'Colour Coded Number') {
    return { cellRenderer: COLOUR_CODED_NUMBER_CELL, isLeftAlign: false };
  }

  if (fieldView.cellType === 'Bonus Status Field') {
    return { cellRenderer: BONUS_STATUS_CELL, isLeftAlign: true };
  }

  if (fieldView.cellType === 'Bonus Reward Field') {
    return { cellRenderer: BONUS_REWARD_CELL, isLeftAlign: true };
  }

  if (fieldView.cellType === 'Bonus Expectation Field') {
    return { cellRenderer: BONUS_EXPECTATION_CELL, isLeftAlign: false };
  }

  if (fieldView.cellType === 'Bonus Eligibility Status') {
    return { cellRenderer: BONUS_QUALIFIED_CELL, isLeftAlign: false };
  }

  if (isEntityField) {
    return { cellRenderer: ENTITY_CELL, isLeftAlign: true };
  }

  if (isLaneCell) {
    return { cellRenderer: 'laneCell', isLeftAlign: true };
  }

  if (fieldView.field === 'orderNo' || fieldView.field === 'jobNo') {
    return { cellRenderer: 'jobCell', isLeftAlign: true };
  }

  if (fieldView.field === 'driver') {
    return { cellRenderer: 'driverCell', isLeftAlign: true };
  }

  if (fieldView.field === 'customer' && isCustomerLaneBoard) {
    return { cellRenderer: 'customerWithHoverMenuCell', isLeftAlign: true };
  }

  if (fieldView.cellType === 'Status Flag') {
    return { cellRenderer: 'statusFlagCell', isLeftAlign: true };
  }

  if (fieldView.cellType === 'Currency') {
    return { cellRenderer: 'currencyCell', isLeftAlign: false };
  }

  if (fieldView.cellType === 'Percentage') {
    return { cellRenderer: 'percentageCell', isLeftAlign: false };
  }

  if (fieldView.cellType === 'Number') {
    return { cellRenderer: NUMBER_CELL, isLeftAlign: false };
  }

  if (fieldView.cellType === 'GeoPoint') {
    return { cellRenderer: GEO_POINT_CELL, isLeftAlign: false };
  }

  if (fieldView.cellType === 'Date') {
    return { cellRenderer: DATE_CELL, isLeftAlign: true };
  }

  if (fieldView.cellType === 'Boolean Icon') {
    return { cellRenderer: BOOLEAN_ICON_CELL, isLeftAlign: true };
  }

  if (fieldView.cellType === undefined && fieldView.aggFunc === 'sum') {
    return { cellRenderer: NUMBER_CELL, isLeftAlign: false };
  }

  if (
    fieldView.field === 'commissionStatus' &&
    fieldView.dataset === 'commission'
  ) {
    return {
      cellRenderer: 'salesCommissionCell',
      isLeftAlign: true,
    };
  }

  if (fieldView.field === 'reasonCode') {
    return {
      cellRenderer: 'reasonCodeCell',
      isLeftAlign: true,
    };
  }

  return {
    cellRenderer: undefined,
    isLeftAlign: true,
  };
};

export const BULK_SALES_COMM_CELL = 'bulkSalesCell';

const BULK_SALES_COMM_COL_DEF = {
  // Its own id, so it keeps its place when other columns are moved
  colId: BULK_SALES_COMM_CELL,
  cellRenderer: BULK_SALES_COMM_CELL,
  isLeftAlign: true,
};

/**
 * Entry point for creating column defs on:
 * - Simple Grid
 * - Popup Grid
 * - Data Manager Search Screen
 * - Customer Lane Commits Boards
 * - Driver Performance Boards (Old bonus system at navajo / NCI)
 */
const useColumnDefs = (
  baseView: FleetOps.BaseView,
  showInvisible?: boolean,
  sort?: SimpleGridSort[],
  disableAutoLayout?: boolean,
): ColDef[] => {
  const { currentPermissions } = useContext(RolesContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  /**
   * This board is confusing as it is possibly undefined, despite
   * typescript saying it is present
   */
  const { board } = useContext(BoardContext);
  const { entityField: performanceEntityField } = useContext(
    PerformanceBoardContexts.SelectedEntity,
  );
  const { getEntityAndDefaultAppForField } = useContext(
    EntityDefinitionsContext,
  );
  const { unitsLocale } = useContext(AccountContext);
  const [colDefs, setColDefs] = useState<ColDef[]>([]);

  useEffect(() => {
    const visibleFieldsBase = showInvisible
      ? getBaseViewFields(baseView)
      : getBaseViewFields(baseView).filter((field) => {
          return field && field.isVisible;
        });
    const visibleFields = baseView.fieldOrder
      .map((f) => visibleFieldsBase.find((vf) => vf.field === f))
      .filter((f) => !!f) as FleetOps.BaseViewField[];

    const newColDefs = visibleFields.map((fieldView) => {
      if (baseView.type === CUSTOMER_LANE_COMMITMENTS) {
        const sf = getLaneCommitCols().find(
          (c) => c.field === _.startCase(fieldView.field),
        );
        if (sf) {
          return sf;
        }
      }

      const isLaneCell =
        fieldView.field === 'lane' &&
        (fieldView.dataset === 'customerLaneCommits' ||
          fieldView.dataset === CUSTOMER_LANE_COMMITMENTS);

      const sortOrder = sort && sort.find((s) => s.field === fieldView.field);
      const entityDefinition = getEntityAndDefaultAppForField({
        field: fieldView.field,
      });
      const { cellRenderer, isLeftAlign } = getCellRenderer({
        isLaneCell,
        isCustomerLaneBoard: isCustomerLaneBoard(board),
        fieldView,
        isPerformanceBoard: isPerformanceBoard(board),
        entityField: performanceEntityField,
        board,
        isEntityField: !!entityDefinition,
        getEntityAndDefaultAppForField,
      });
      const fieldType = getFieldType({
        datasets,
        datasetType: fieldView.dataset,
        fieldName: fieldView.field,
      });
      const cellRendererParams: FleetOpsCellRendererParams = {
        isLeftAlign,
        fieldView,
        fieldType,
        entityDefinition,
      };
      return {
        headerName: fieldView.nameAlias
          ? fieldView.nameAlias
          : _.startCase(fieldView.field),
        field: fieldView.field,
        sort: sortOrder ? sortOrder.direction : undefined,
        sortable: cellRenderer === GEO_POINT_CELL ? false : true,
        valueFormatter: defaultFormatter,
        type: getType(fieldType),
        menuTabs: [],
        wrapHeaderText: true,
        autoHeaderHeight: true,
        flex: disableAutoLayout ? 1 : undefined,
        minWidth: disableAutoLayout ? 80 : undefined,
        rowGroup: false,
        enableRowGroup: true,
        editable: fieldView.field === 'reasonCode',
        cellEditor:
          fieldView.field === 'reasonCode' ? 'reasonCodePicker' : undefined,
        cellEditorPopup: fieldView.field === 'reasonCode',
        pinned: isCustomerLaneBoard(board)
          ? fieldView.field === 'customer' || fieldView.field === 'lane'
          : false,
        cellRenderer,
        headerClass: isLeftAlign ? undefined : 'rightAlignedHeader',
        cellRendererParams,
        valueGetter: (params) => {
          const dotSections = fieldView.field.split('.');
          if (dotSections.length === 2) {
            const lhs = dotSections[0];
            const rhs = dotSections[1];

            const values: string[] = [];
            const value = params.data[lhs] as
              | {
                  [key: string]: string | undefined;
                }[]
              | {
                  [key: string]: string | undefined;
                }
              | string
              | undefined;
            try {
              if (value === undefined) {
                return '-';
              } else if (Array.isArray(value)) {
                value.forEach((t) => {
                  const v = t[rhs];
                  if (v) {
                    values.push(v);
                  }
                });

                if (values.length === 0) {
                  return '-';
                }
                return values;
              } else if (typeof value === 'object') {
                const v = value[rhs];
                return v;
              } else {
                return value;
              }
            } catch (ex) {
              captureException(ex);
            }
          }

          return params.data[fieldView.field];
        },
      };
    }) as ColDef[];

    if (board && isGeneralBoard(board) && board.specialCells) {
      board.specialCells
        .map((cell) => {
          if (cell.type === 'OnTarget') {
            return {
              field: getSpecialCellField(cell),
              type: 'numericColumn',
              valueFormatter: defaultFormatter,
              cellRenderer: 'onTargetCell',
              minWidth: 80,
              wrapHeaderText: true,
              autoHeaderHeight: true,
              cellRendererParams: {
                cell,
                fieldView: (() => {
                  const maybeF = getBaseViewFields(baseView).find(
                    (f) => f.field === cell.differenceField,
                  );
                  if (maybeF) {
                    return maybeF;
                  }
                  return undefined;
                })(),
              },
            };
          }

          if (cell.type === PROGRESS_CELL_TYPE) {
            return buildProgressCell({
              cell,
              baseView,
            });
          }

          return undefined;
        })
        .filter(isDefined)
        .forEach((cell) => {
          newColDefs.push(cell);
        });
    }

    if (board) {
      const newDefs = [] as ColDef[];
      if (
        board.dataType === 'commission' &&
        currentPermissions.includes(PERMISSIONS.INTERNAL_USE.MODIFY_COMMISSIONS)
      ) {
        newDefs.push(BULK_SALES_COMM_COL_DEF);
      }
      newDefs.push(
        ...board.columnOrder
          .filter((col) => {
            if (isPerformanceColumn(col)) {
              return col.type === 'field';
            }

            return true;
          })
          .map((co) => {
            return newColDefs.find((cd) => cd.field === co.field && co.visible);
          })
          .filter(isDefined),
      );
      if (isPerformanceBoard(board)) {
        board.columnOrder
          .filter((f) => f.type === 'progress' && f.visible)
          .forEach((progressField) => {
            const cell: BoardProgressCell = {
              type: 'Progress',
              totalField: progressField.field,
              targetField: `${progressField.field}Target`,
              differenceField: `${progressField.field}Difference`,
              headerName: `${progressField.field} Progress`,
              tooltipTitle: `${progressField.field} Progress`,
            };
            newDefs.push(
              buildProgressCell({
                cell,
                baseView,
              }),
            );
          });
      }

      setColDefs(newDefs);
    } else {
      setColDefs(newColDefs);
    }
  }, [
    baseView,
    baseView.fields,
    baseView.type,
    board,
    datasets,
    disableAutoLayout,
    getEntityAndDefaultAppForField,
    currentPermissions,
    performanceEntityField,
    showInvisible,
    sort,
    unitsLocale,
  ]);

  return colDefs;
};

export default useColumnDefs;
